import React, { useEffect, useMemo, useRef, useState } from "react";
import YhMultiSelect from "./YhMultiSelect";
import YhSelect from "./YhSelect";
import { YhDateRangePicker, YhDateRangeValue } from "./YhDateTimePicker";
import YhButton from "./YhButton";

export type LeadFilterValues = {
  formIds: string[];
  sources: string[];
  stages: string[];
  assignment: string;
  uploadLeadIds: string[];
  dateRange: YhDateRangeValue;
  /** Assigned member filter (All Leads) — multi */
  memberIds?: string[];
  /** Department multi-select */
  departmentIds?: string[];
  /** Team / sub-team multi-select */
  teamIds?: string[];
  /** Fresh = created today */
  fresh?: boolean;
  /** Overdue reminders / untouched leads */
  attention?: boolean;
  untouchedDays?: number;
};

export const emptyLeadFilterValues = (): LeadFilterValues => ({
  formIds: [],
  sources: [],
  stages: [],
  assignment: "all",
  uploadLeadIds: [],
  dateRange: { from: null, to: null },
  memberIds: [],
  departmentIds: [],
  teamIds: [],
  fresh: false,
  attention: false,
  untouchedDays: 3,
});

export function countActiveLeadFilters(
  v: LeadFilterValues,
  opts?: {
    hideAssignment?: boolean;
    hideBulkSet?: boolean;
    hideMember?: boolean;
    hideDepartment?: boolean;
    hideTeam?: boolean;
    hideSource?: boolean;
    hideFresh?: boolean;
    hideAttention?: boolean;
  }
): number {
  let n = 0;
  if (v.formIds?.length) n += 1;
  if (!opts?.hideSource && v.sources?.length) n += 1;
  if (v.stages?.length) n += 1;
  if (!opts?.hideAssignment && v.assignment && v.assignment !== "all") n += 1;
  if (!opts?.hideBulkSet && v.uploadLeadIds?.length) n += 1;
  if (!opts?.hideDepartment && v.departmentIds?.length) n += 1;
  if (!opts?.hideTeam && v.teamIds?.length) n += 1;
  if (!opts?.hideMember && v.memberIds?.length) n += 1;
  if (v.dateRange?.from || v.dateRange?.to) n += 1;
  if (!opts?.hideFresh && v.fresh) n += 1;
  if (!opts?.hideAttention && v.attention) n += 1;
  return n;
}

type Option = { label: string; value: string };

type Props = {
  open: boolean;
  onClose: () => void;
  value: LeadFilterValues;
  onApply: (next: LeadFilterValues) => void;
  onClear: () => void;
  formOptions: Option[];
  sourceOptions: Option[];
  stageOptions: Option[];
  bulkSetOptions?: Option[];
  /** Org members fallback when no team selected */
  memberOptions?: Option[];
  departmentOptions?: Option[];
  teamOptions?: Option[];
  /** Called when draft department selection changes (parent refreshes team options) */
  onDepartmentIdsChange?: (ids: string[]) => void;
  /** Called when draft team selection changes (parent refreshes member options) */
  onTeamIdsChange?: (ids: string[]) => void;
  /** Cascaded members for selected teams (preferred over memberOptions) */
  teamMemberOptions?: Option[];
  /** Single-select member instead of multi */
  memberSingleSelect?: boolean;
  /** Hide assignment (e.g. personal My Lead for non-admin) */
  hideAssignment?: boolean;
  hideBulkSet?: boolean;
  /** Hide stage selector */
  hideStage?: boolean;
  hideDepartment?: boolean;
  hideTeam?: boolean;
  hideMember?: boolean;
  hideSource?: boolean;
  /** Show Fresh toggle (Team Leads) */
  showFresh?: boolean;
  /** Show Overdue / untouched (Team Leads) */
  showAttention?: boolean;
  title?: string;
};

/**
 * Right-side filter drawer for All Leads / My Lead.
 * Edits a draft until Apply — keeps the toolbar compact.
 */
const LeadFiltersDrawer: React.FC<Props> = ({
  open,
  onClose,
  value,
  onApply,
  onClear,
  formOptions,
  sourceOptions,
  stageOptions,
  bulkSetOptions = [],
  memberOptions = [],
  departmentOptions = [],
  teamOptions = [],
  teamMemberOptions,
  onDepartmentIdsChange,
  onTeamIdsChange,
  memberSingleSelect = false,
  hideAssignment = false,
  hideBulkSet = false,
  hideStage = false,
  hideDepartment = false,
  hideTeam = false,
  hideMember = false,
  hideSource = false,
  showFresh = false,
  showAttention = false,
  title = "Filters",
}) => {
  const [draft, setDraft] = useState<LeadFilterValues>(value);
  const wasOpenRef = useRef(false);
  const valueRef = useRef(value);
  valueRef.current = value;

  // Only hydrate draft when the drawer opens. Never reset while open —
  // parent re-renders (team/member option loads) must not wipe selections.
  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    if (!wasOpenRef.current) {
      setDraft(valueRef.current);
      wasOpenRef.current = true;
    }
  }, [open]);

  const patch = (partial: Partial<LeadFilterValues>) =>
    setDraft((prev) => ({ ...prev, ...partial }));

  const showDepartment =
    !hideDepartment && (departmentOptions || []).length > 0;
  const showTeam = !hideTeam && (showDepartment || (teamOptions || []).length > 0);
  // Dept → Team → Member: only list members after a team is selected
  const cascadedMembers =
    showTeam || showDepartment
      ? (draft.teamIds || []).length > 0
        ? teamMemberOptions && teamMemberOptions.length > 0
          ? teamMemberOptions
          : []
        : (teamMemberOptions || []).filter((o) =>
            (draft.memberIds || []).includes(String(o.value))
          )
      : memberOptions || [];
  const showMember =
    !hideMember &&
    cascadedMembers.length > 0 &&
    (showTeam || showDepartment
      ? (draft.teamIds || []).length > 0 || (draft.memberIds || []).length > 0
      : true);

  const activeCount = useMemo(
    () =>
      countActiveLeadFilters(draft, {
        hideAssignment,
        hideBulkSet,
        hideMember: !showMember,
        hideDepartment: !showDepartment,
        hideTeam: !showTeam,
        hideSource,
        hideFresh: !showFresh,
        hideAttention: !showAttention,
      }),
    [
      draft,
      hideAssignment,
      hideBulkSet,
      showMember,
      showDepartment,
      showTeam,
      hideSource,
      showFresh,
      showAttention,
    ]
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex justify-end">
      <button
        type="button"
        aria-label="Close filters"
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative z-[1] flex h-full w-full max-w-[420px] flex-col bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-[#111827]">{title}</h2>
            <p className="text-xs text-[#6B7280]">
              {activeCount
                ? `${activeCount} filter${activeCount === 1 ? "" : "s"} selected`
                : "Refine the lead list"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#111827]"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {showFresh ? (
            <div>
              <label className="yh-label mb-1 block text-sm font-medium text-[#374151]">
                Fresh
              </label>
              <button
                type="button"
                onClick={() => patch({ fresh: !draft.fresh })}
                className={`flex h-10 w-full items-center justify-between rounded-xl border px-3 text-left text-sm font-medium transition ${
                  draft.fresh
                    ? "border-emerald-600 bg-emerald-50 text-emerald-700"
                    : "border-[#E5E7EB] bg-white text-[#374151] hover:border-[#D4B8C0]"
                }`}
              >
                <span>Arrived today</span>
                <span
                  className={`text-xs font-semibold ${
                    draft.fresh ? "text-emerald-700" : "text-[#9CA3AF]"
                  }`}
                >
                  {draft.fresh ? "On" : "Off"}
                </span>
              </button>
            </div>
          ) : null}

          {showAttention ? (
            <div className="space-y-2">
              <label className="yh-label mb-1 block text-sm font-medium text-[#374151]">
                Overdue / untouched
              </label>
              <button
                type="button"
                onClick={() => patch({ attention: !draft.attention })}
                className={`flex h-10 w-full items-center justify-between rounded-xl border px-3 text-left text-sm font-medium transition ${
                  draft.attention
                    ? "border-[#841C39] bg-[#F9F0F4] text-[#841C39]"
                    : "border-[#E5E7EB] bg-white text-[#374151] hover:border-[#D4B8C0]"
                }`}
              >
                <span>Needs attention</span>
                <span
                  className={`text-xs font-semibold ${
                    draft.attention ? "text-[#841C39]" : "text-[#9CA3AF]"
                  }`}
                >
                  {draft.attention ? "On" : "Off"}
                </span>
              </button>
              {draft.attention ? (
                <label className="flex items-center gap-2 text-xs text-[#4D5369]">
                  Untouched days
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={draft.untouchedDays ?? 3}
                    onChange={(e) =>
                      patch({
                        untouchedDays: Math.max(
                          1,
                          Number(e.target.value) || 3
                        ),
                      })
                    }
                    className="w-16 rounded-lg border border-[#E5E7EB] px-2 py-1.5 text-xs"
                  />
                </label>
              ) : null}
            </div>
          ) : null}

          {showDepartment ? (
            <div>
              <YhMultiSelect
                label="Department"
                placeholder="All departments"
                searchPlaceholder="Search departments..."
                showOptionIcon={false}
                clearable
                showSelectAll
                className="!w-full"
                options={departmentOptions}
                value={draft.departmentIds || []}
                onChange={(values) => {
                  const ids = values.map(String).filter(Boolean);
                  patch({
                    departmentIds: ids,
                    teamIds: [],
                    memberIds: [],
                  });
                  onDepartmentIdsChange?.(ids);
                  onTeamIdsChange?.([]);
                }}
              />
            </div>
          ) : null}

          {showTeam ? (
            <div>
              <YhMultiSelect
                label="Team"
                placeholder={
                  (draft.departmentIds || []).length
                    ? "All teams in selected departments"
                    : "All teams"
                }
                searchPlaceholder="Search teams..."
                showOptionIcon={false}
                clearable
                showSelectAll
                className="!w-full"
                options={teamOptions}
                value={draft.teamIds || []}
                onChange={(values) => {
                  const ids = values.map(String).filter(Boolean);
                  patch({
                    teamIds: ids,
                    memberIds: [],
                  });
                  onTeamIdsChange?.(ids);
                }}
              />
            </div>
          ) : null}

          {showMember ? (
            <div>
              {memberSingleSelect ? (
                <YhSelect
                  label="Team Member"
                  clearable
                  className="!w-full"
                  placeholder="All members"
                  options={cascadedMembers}
                  value={(draft.memberIds || [])[0] || ""}
                  onChange={(e: any) => {
                    const v = String(
                      e?.target?.value ?? e?.value ?? e ?? ""
                    ).trim();
                    patch({ memberIds: v ? [v] : [] });
                  }}
                />
              ) : (
                <YhMultiSelect
                  label="Team Member"
                  placeholder="All members in selected teams"
                  searchPlaceholder="Search members..."
                  showOptionIcon={false}
                  clearable
                  showSelectAll
                  className="!w-full"
                  options={cascadedMembers}
                  value={draft.memberIds || []}
                  onChange={(values) =>
                    patch({
                      memberIds: values.map(String).filter(Boolean),
                    })
                  }
                />
              )}
            </div>
          ) : showTeam || showDepartment ? (
            <div>
              <label className="yh-label mb-1 block text-sm font-medium text-[#374151]">
                Team Member
              </label>
              <p className="rounded-lg border border-dashed border-[#E5E7EB] px-3 py-2.5 text-xs text-[#9CA3AF]">
                {(draft.departmentIds || []).length === 0
                  ? "Select department, then team"
                  : (draft.teamIds || []).length === 0
                    ? "Select a team first"
                    : "No members on selected teams"}
              </p>
            </div>
          ) : null}

          {!hideAssignment ? (
            <div>
              <YhSelect
                label="Assignment"
                clearable={false}
                className="!w-full"
                options={[
                  { label: "All leads", value: "all" },
                  { label: "Unassigned", value: "unassigned" },
                  { label: "Assigned", value: "assigned" },
                ]}
                value={draft.assignment || "all"}
                onChange={(e: any) => {
                  const v = String(
                    e?.target?.value ?? e?.value ?? e ?? "all"
                  ).trim();
                  patch({ assignment: v || "all" });
                }}
              />
            </div>
          ) : null}

          <div>
            <YhMultiSelect
              label="Form"
              placeholder="All forms"
              searchPlaceholder="Search forms..."
              showOptionIcon={false}
              clearable
              showSelectAll
              className="!w-full"
              options={formOptions}
              value={draft.formIds}
              onChange={(values) =>
                patch({ formIds: values.map(String).filter(Boolean) })
              }
            />
          </div>

          {!hideSource ? (
            <div>
              <YhMultiSelect
                label="Source"
                placeholder="All sources"
                searchPlaceholder="Search sources..."
                showOptionIcon={false}
                clearable
                showSelectAll
                className="!w-full"
                options={sourceOptions}
                value={draft.sources}
                onChange={(values) =>
                  patch({ sources: values.map(String).filter(Boolean) })
                }
              />
            </div>
          ) : null}

          {!hideStage ? (
            <div>
              <YhMultiSelect
                label="Stage"
                placeholder="All stages"
                searchPlaceholder="Search stages..."
                searchable
                showOptionIcon={false}
                clearable
                showSelectAll
                className="!w-full"
                options={stageOptions}
                value={draft.stages}
                onChange={(values) =>
                  patch({ stages: values.map(String).filter(Boolean) })
                }
              />
            </div>
          ) : null}

          {!hideBulkSet ? (
            <div>
              <YhMultiSelect
                label="Bulk upload set"
                placeholder="All datasets"
                searchPlaceholder="Search datasets..."
                showOptionIcon={false}
                clearable
                showSelectAll
                className="!w-full"
                options={bulkSetOptions}
                value={draft.uploadLeadIds}
                onChange={(values) =>
                  patch({ uploadLeadIds: values.map(String).filter(Boolean) })
                }
              />
            </div>
          ) : null}

          <div>
            <label className="yh-label mb-1 block text-sm font-medium text-[#374151]">
              Date range
            </label>
            <YhDateRangePicker
              className="w-full"
              value={draft.dateRange || { from: null, to: null }}
              onChange={(range) => patch({ dateRange: range })}
              placeholder="Date Range"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 border-t border-[#F3F4F6] px-4 py-3">
          <button
            type="button"
            onClick={() => {
              const cleared = emptyLeadFilterValues();
              setDraft(cleared);
              onDepartmentIdsChange?.([]);
              onTeamIdsChange?.([]);
              onClear();
            }}
            className="text-sm font-semibold text-[#A02448] hover:underline"
          >
            Clear all
          </button>
          <div className="flex-1" />
          <YhButton variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            onClick={() => {
              onApply(draft);
              onClose();
            }}
          >
            Apply filters
          </YhButton>
        </div>
      </aside>
    </div>
  );
};

export default LeadFiltersDrawer;
